import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { DelegatingAuthenticationConverter } from '../core/delegating-authentication-converter';
import { BaseHttpClient } from '../core/base-http-client';
import { HttpError } from '../core/http-error';
import { HttpHeaders } from '../core/http-headers';
import { HttpMessageBody } from '../core/http-message-body';
import { HttpMethod } from '../core/http-method';
import { HttpRequest } from '../core/http-request';
import { HttpResponse } from '../core/http-response';
import { HttpRequestBody } from '../core/http-request-body';

export class AxiosHttpClient extends BaseHttpClient {
  constructor(
    baseUrl = '',
    authenticationConverter: DelegatingAuthenticationConverter = new DelegatingAuthenticationConverter()
  ) {
    super(baseUrl, authenticationConverter);
  }

  async sendRequest<ResponseBody extends HttpMessageBody = any>(
    request: HttpRequest<HttpRequestBody>,
    axiosClient: AxiosInstance = axios
  ): Promise<HttpResponse<ResponseBody>> {
    try {
      process.env['NODE_TLS_REJECT_UNAUTHORIZED'] = '0';
      const { urlWithoutQueryParams, queryParams: urlQueryParams } = this.getUrl(request);
      const headers = this.getHeaders(request);
      const axiosRequestMethod = this.getAxiosRequestMethod(request.method);
      const timeout = request.timeout ? request.timeout : 0;
      const responseType = request.responseType || 'json';
      const queryParams = request.queryParams || {}

      for (const [key, value] of urlQueryParams) {
        queryParams[key] = value
      }

      const config: AxiosRequestConfig = {
        method: axiosRequestMethod,
        url: urlWithoutQueryParams,
        params: queryParams,
        headers,
        data: request.body,
        timeout,
        responseType,
      };

      if (request.followRedirects === false) {
        config.maxRedirects = 0;
        config.validateStatus = (status) => {
          if (status >= 400) return false;
          return status >= 200 && status < 400;
        };
      }

      if (request.retries && request.retries > 0) {
        axiosRetry(axiosClient, {
          retries: request.retries,
          retryDelay: axiosRetry.exponentialDelay,
          retryCondition: (error) => {
            return axiosRetry.isNetworkOrIdempotentRequestError(error) || (error.response && error.response.status >= 500) || false;
          },
        });
      }

      const response = await axiosClient.request(config);

      return {
        status: response.status,
        headers: response.headers as HttpHeaders,
        body: response.data,
      };
    } catch (e) {
      // Only pieces that load this build get what follows. In Lambda, the engine runs each npm piece
      // with its own pieces-common copy (restored by lambda/Dockerfile), whose failed requests still leak.
      if (axios.isAxiosError(e)) {
        // Never log e itself: its config carries the request headers and body
        console.error('[HttpClient#sendRequest] error:', e.code, e.message);
        // Not the response body either: some APIs echo the submitted fields back in it.
        // It stays in the HttpError message, which the engine prints when the step fails.
        console.error(
          '[HttpClient#sendRequest] error, responseStatus:',
          e.response?.status
        );

        throw new HttpError(request.body, e);
      }

      throw e;
    }
  }

  private getAxiosRequestMethod(httpMethod: HttpMethod): string {
    return httpMethod.toString();
  }
}
